import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Top-level React error boundary. Catches render-time errors so the whole
 * app doesn't unmount and show a blank screen. Offers two recovery options:
 *
 *   - "Try again" — clears the error state without a full reload. Best for
 *     transient errors (e.g. failed lazy chunk on first try).
 *   - "Reload page" — last-resort hard refresh. Reissues CSRF cookies and
 *     fetches fresh JS chunks.
 *
 * Also tags `chunkLoadError` specifically: lazy() chunks can 404 after a
 * deploy because filename hashes change. We treat that as "your app version
 * is out of date" and prompt for a reload, which fetches the new index.html
 * + chunk manifest.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // TODO: forward to Sentry / your error tracker
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleRetry = () => {
    // Soft retry: clear error so children re-render. Helpful when the error
    // was triggered by stale state that's since been corrected.
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const err = this.state.error;
    const isChunkLoad =
      err?.name === 'ChunkLoadError' ||
      /Loading chunk \d+ failed/i.test(err?.message || '') ||
      /Loading CSS chunk/i.test(err?.message || '');

    return (
      <div
        role="alert"
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h1 style={{ marginBottom: '0.5rem' }}>
          {isChunkLoad ? 'A newer version is available' : 'Something went wrong'}
        </h1>
        <p style={{ color: '#666', marginBottom: '1.5rem', maxWidth: 480 }}>
          {isChunkLoad
            ? 'The page failed to load because the app was updated. Reload to pick up the latest version.'
            : 'We hit an unexpected problem. Please try again. If this keeps happening, contact support.'}
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          {!isChunkLoad && (
            <button
              onClick={this.handleRetry}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#fff',
                color: '#667eea',
                border: '1px solid #667eea',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              Try again
            </button>
          )}
          <button
            onClick={this.handleReload}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#667eea',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            Reload page
          </button>
          <Link
            to="/"
            onClick={this.handleRetry}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'transparent',
              color: '#666',
              textDecoration: 'none',
              borderRadius: 6,
              fontWeight: 500,
              alignSelf: 'center',
            }}
          >
            Go home
          </Link>
        </div>
        {process.env.NODE_ENV === 'development' && err && (
          <pre
            style={{
              marginTop: '2rem',
              maxWidth: '90%',
              overflow: 'auto',
              background: '#f5f5f5',
              padding: '1rem',
              borderRadius: 6,
              textAlign: 'left',
              fontSize: 12,
            }}
          >
            {String(err?.stack || err)}
          </pre>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
